import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  AuthLayout,
  AuthPanel,
  AuthPanelDescription,
  AuthPanelHeader,
  AuthPanelTitle,
} from '@/components/auth-layout';
import { FieldError } from '@/components/field-error';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { callAdmin } from '@/lib/api';
import {
  currentStage,
  type Enrolment,
  enrolAuthenticator,
  type SignInStage,
  signInWithPasskey,
  signInWithPassword,
  verifyCode,
} from '@/lib/auth';
import { supabaseConfigured } from '@/lib/supabase';

const searchSchema = z.object({
  reason: z.enum(['expired', 'disabled', 'not_staff', 'signed_out']).optional(),
});

export const Route = createFileRoute('/sign-in')({
  validateSearch: searchSchema,
  component: SignInPage,
});

const REASONS: Record<string, string> = {
  expired: 'You were signed out after a while. Sign in again.',
  disabled: 'This staff account is disabled.',
  not_staff: 'This account is not a Sao staff account.',
  signed_out: 'You are signed out.',
};

function SignInPage() {
  const { reason } = Route.useSearch();
  const navigate = useNavigate();
  const [stage, setStage] = useState<SignInStage | null>(null);

  useEffect(() => {
    void currentStage().then(setStage);
  }, []);

  useEffect(() => {
    if (stage?.stage === 'done') void navigate({ to: '/' });
  }, [stage, navigate]);

  return (
    <AuthLayout>
      {supabaseConfigured ? null : (
        <Alert variant="warning">
          The portal is not configured: set VITE_SUPABASE_URL and
          VITE_SUPABASE_PUBLISHABLE_KEY.
        </Alert>
      )}
      {reason && stage?.stage === 'signed_out' ? (
        <Alert variant="info">{REASONS[reason]}</Alert>
      ) : null}
      {stage ? <StageStep stage={stage} onStage={setStage} /> : null}
    </AuthLayout>
  );
}

/** The card for where this sign-in has got to. */
function StageStep({
  stage,
  onStage,
}: Readonly<{ stage: SignInStage; onStage: (stage: SignInStage) => void }>) {
  const [mode, setMode] = useState<'password' | 'link'>('password');
  const done = () => onStage({ stage: 'done' });

  switch (stage.stage) {
    case 'signed_out':
      return mode === 'password' ? (
        <PasswordStep onDone={onStage} onLink={() => setMode('link')} />
      ) : (
        <LinkStep onBack={() => setMode('password')} />
      );
    case 'enrol':
      return <EnrolStep onDone={done} />;
    case 'verify':
      return <VerifyStep factorId={stage.factorId} onDone={done} />;
    default:
      return null;
  }
}

function PasswordStep({
  onDone,
  onLink,
}: Readonly<{
  onDone: (stage: SignInStage) => void;
  onLink: () => void;
}>) {
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: { email: '', password: '' },
    validators: {
      onSubmit: z.object({
        email: z.email('Enter your work email.'),
        password: z.string().min(1, 'Enter your password.'),
      }),
    },
    onSubmit: async ({ value }) => {
      setError(null);
      try {
        onDone(await signInWithPassword(value.email, value.password));
      } catch (error_) {
        setError((error_ as Error).message);
      }
    },
  });

  return (
    <AuthPanel>
      <AuthPanelHeader>
        <AuthPanelTitle>Sign in</AuthPanelTitle>
        <AuthPanelDescription>
          With your Sao staff email. Customer logins do not work here.
        </AuthPanelDescription>
      </AuthPanelHeader>
      <div>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.Field name="email">
            {(field) => (
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>
          <form.Field name="password">
            {(field) => (
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} />
              </div>
            )}
          </form.Field>
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(submitting) => (
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            )}
          </form.Subscribe>
          <Button variant="link" onClick={onLink}>
            First time, or forgot your password? Email me a link
          </Button>
        </form>
        <div className="mt-4 border-t border-border pt-4">
          <PasskeySignIn onDone={onDone} />
        </div>
      </div>
    </AuthPanel>
  );
}

function LinkStep({ onBack }: Readonly<{ onBack: () => void }>) {
  const [email, setEmail] = useState('');
  const request = useMutation({
    mutationFn: (to: string) => callAdmin('auth.request_link', { email: to }),
  });
  // One press, one email: each request counts against the hourly limit, so
  // the button stays off for this address once its link is on its way.
  const sentToThis = request.isSuccess && request.variables === email;

  return (
    <AuthPanel>
      <AuthPanelHeader>
        <AuthPanelTitle>Email me a link</AuthPanelTitle>
        <AuthPanelDescription>
          If this email belongs to a Sao staff member, a link to set a password
          arrives within a few minutes.
        </AuthPanelDescription>
      </AuthPanelHeader>
      <div>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!sentToThis) request.mutate(email);
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="link-email">Email</Label>
            <Input
              id="link-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          {request.isSuccess ? (
            <Alert variant="success">
              If the email is a staff email, the link is on its way.
            </Alert>
          ) : null}
          {request.isError ? (
            <Alert variant="destructive">{request.error.message}</Alert>
          ) : null}
          <Button
            type="submit"
            disabled={request.isPending || sentToThis || !email}
          >
            {sentToThis ? 'Link sent' : 'Send the link'}
          </Button>
          <Button variant="link" onClick={onBack}>
            Back to sign in
          </Button>
        </form>
      </div>
    </AuthPanel>
  );
}

function EnrolStep({ onDone }: Readonly<{ onDone: () => void }>) {
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    enrolAuthenticator().then(setEnrolment, (error_: Error) =>
      setError(error_.message),
    );
  }, []);

  return (
    <AuthPanel>
      <AuthPanelHeader>
        <AuthPanelTitle>Add an authenticator app</AuthPanelTitle>
        <AuthPanelDescription>
          Scan this with Microsoft Authenticator, Google Authenticator or
          1Password. You need the code every time you sign in.
        </AuthPanelDescription>
      </AuthPanelHeader>
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        {enrolment ? (
          <>
            <img
              src={enrolment.qrCode}
              alt="Authenticator QR code"
              className="mx-auto size-44 rounded-md bg-white p-2"
            />
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                Or type this key
              </span>
              <code
                data-testid="totp-secret"
                className="rounded bg-surface-2 px-2 py-1 font-mono text-sm break-all"
              >
                {enrolment.secret}
              </code>
            </div>
            <CodeForm factorId={enrolment.factorId} onDone={onDone} />
          </>
        ) : null}
      </div>
    </AuthPanel>
  );
}

function VerifyStep({
  factorId,
  onDone,
}: Readonly<{
  factorId: string;
  onDone: () => void;
}>) {
  return (
    <AuthPanel>
      <AuthPanelHeader>
        <AuthPanelTitle>Enter your code</AuthPanelTitle>
        <AuthPanelDescription>
          The six digits your authenticator app shows for Sao Staff.
        </AuthPanelDescription>
      </AuthPanelHeader>
      <div>
        <CodeForm factorId={factorId} onDone={onDone} />
      </div>
    </AuthPanel>
  );
}

/** Signs in with a passkey alone: no email, no password, no code. */
function PasskeySignIn({
  onDone,
}: Readonly<{ onDone: (stage: SignInStage) => void }>) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  async function run() {
    setError(null);
    setPending(true);
    try {
      onDone(await signInWithPasskey());
    } catch (error_) {
      setError((error_ as Error).message);
    } finally {
      setPending(false);
    }
  }
  return (
    <div className="flex flex-col gap-2">
      {error ? <Alert variant="destructive">{error}</Alert> : null}
      <Button variant="outline" pending={pending} onClick={() => void run()}>
        Sign in with a passkey
      </Button>
    </div>
  );
}

function CodeForm({
  factorId,
  onDone,
}: Readonly<{
  factorId: string;
  onDone: () => void;
}>) {
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: { code: '' },
    validators: {
      onSubmit: z.object({
        code: z
          .string()
          .regex(/^\s*\d{3}\s*\d{3}\s*$/, 'Enter the six digits.'),
      }),
    },
    onSubmit: async ({ value }) => {
      setError(null);
      try {
        await verifyCode(factorId, value.code);
        onDone();
      } catch (error_) {
        setError((error_ as Error).message);
      }
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.Field name="code">
        {(field) => (
          <div className="flex flex-col gap-2">
            <Label htmlFor="code">Code</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
            />
            <FieldError errors={field.state.meta.errors} />
          </div>
        )}
      </form.Field>
      {error ? <Alert variant="destructive">{error}</Alert> : null}
      <form.Subscribe selector={(state) => state.isSubmitting}>
        {(submitting) => (
          <Button type="submit" disabled={submitting}>
            Confirm
          </Button>
        )}
      </form.Subscribe>
    </form>
  );
}
